import { useEffect, useState } from 'react';
import { Typography, Button, Checkbox, Tooltip } from 'antd';
import styled from '@emotion/styled';
import { PlusIcon } from '@phosphor-icons/react';
import { TagFamilyMemberType } from '../../../../../schemas';
import {
  addChildAtPath,
  defaultNode,
  deleteNodeByPath,
  ROOT_NODE_NAME,
  updateChildrenAtPath,
  updateNodeByPath,
} from './utils';
import TagChildrenEditor from './TagChildrenEditor';
import { useAppSelector } from '../../../../../store/store';

const DottedBgWrapper = styled.div`
  border: 1px solid #f4f4f4;
  background: #ffffff;
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
  overflow: auto;
  background-image:
    radial-gradient(#f4f4f4 1.5px, transparent 1.5px),
    radial-gradient(#f4f4f4 1.5px, transparent 1.5px);
  background-size: 16px 16px;
  background-position:
    0 0,
    8px 8px;
`;

interface ITagsFamilyProps {
  data?: Array<TagFamilyMemberType>;
  onChange?: (tags: Array<TagFamilyMemberType>) => void;
}

const TagsFamily = ({ data = [], onChange }: ITagsFamilyProps) => {
  const isWrappedRoot =
    data.length === 1 && data[0].name === ROOT_NODE_NAME && Array.isArray(data[0].children);

  const initialTree = isWrappedRoot
    ? (data[0].children ?? [defaultNode()])
    : data && data.length > 0
      ? data
      : [defaultNode()];

  const [tree, setTree] = useState<Array<TagFamilyMemberType>>(initialTree);
  const [rootAllowMultiple, setRootAllowMultiple] = useState<boolean>(
    isWrappedRoot ? !!data[0].allowMultiple : false,
  );

  const workflowPayload = useAppSelector(state => state.workflows.workflowCreatePayload);
  const isUpdateMode = Boolean(workflowPayload && 'id' in workflowPayload && workflowPayload.id);

  useEffect(() => {
    const isWrappedRoot =
      data.length === 1 && data[0].name === ROOT_NODE_NAME && Array.isArray(data[0].children);
    setTree(
      isWrappedRoot
        ? (data[0].children ?? [defaultNode()])
        : data && data.length > 0
          ? data
          : [defaultNode()],
    );
    setRootAllowMultiple(isWrappedRoot ? !!data[0].allowMultiple : false);
  }, [data]);

  // Wrap tree in root node for API
  const propagateChange = (
    newTree: Array<TagFamilyMemberType>,
    allowMultiple = rootAllowMultiple,
  ) => {
    onChange?.([
      {
        name: ROOT_NODE_NAME,
        allowMultiple,
        children: newTree,
      },
    ]);
  };

  const handleNodeChange = (
    path: Array<number>,
    newNode: TagFamilyMemberType | Array<TagFamilyMemberType>,
  ) => {
    const newTree = updateNodeByPath(path, tree, newNode);
    setTree(newTree);
    propagateChange(newTree);
  };

  const handleNodeDelete = (path: Array<number>) => {
    const newTree = deleteNodeByPath(path, tree);
    if (newTree.length === 0 && path.length === 1) {
      const resetTree = [defaultNode()];
      setTree(resetTree);
      propagateChange(resetTree);
    } else {
      setTree(newTree);
      propagateChange(newTree);
    }
  };

  const handleAddChild = (path: Array<number>, child: TagFamilyMemberType) => {
    const newTree = addChildAtPath(path, tree, child);
    setTree(newTree);
    propagateChange(newTree);
  };

  const handleSort = (path: Array<number>, newNodes: Array<TagFamilyMemberType>) => {
    if (path.length === 0) {
      setTree(newNodes);
      propagateChange(newNodes);
    } else {
      const newTree = updateChildrenAtPath(path, tree, newNodes);
      setTree(newTree);
      propagateChange(newTree);
    }
  };

  const handleRootAllowMultipleChange = (checked: boolean) => {
    setRootAllowMultiple(checked);
    propagateChange(tree, checked);
  };

  return (
    <DottedBgWrapper>
      <Typography.Text strong style={{ marginBottom: 16, display: 'block' }}>
        Tags Family
      </Typography.Text>
      <Tooltip title={isUpdateMode ? 'Cannot change this in edit mode.' : undefined}>
        <Checkbox
          checked={rootAllowMultiple}
          onChange={e => handleRootAllowMultipleChange(e.target.checked)}
          style={{ marginBottom: 16 }}
          disabled={isUpdateMode}
        >
          Allow Multiple
        </Checkbox>
      </Tooltip>
      <div
        style={{
          background: 'transparent',
          marginBottom: 16,
        }}
      >
        <TagChildrenEditor
          nodes={tree}
          parentPath={[]}
          onChange={handleNodeChange}
          onDelete={handleNodeDelete}
          onAddChild={handleAddChild}
          onSort={handleSort}
          level={0}
        />
        <Button
          icon={<PlusIcon />}
          type="dashed"
          style={{ marginTop: 16 }}
          onClick={() => {
            const newTree = [...tree, defaultNode()];
            setTree(newTree);
            propagateChange(newTree);
          }}
        >
          Add Root Tag
        </Button>
      </div>
    </DottedBgWrapper>
  );
};

export default TagsFamily;
